/**
 * SearchResultTabs — 검색결과를 구글식 탭으로.
 *   전체(현행 종합 스택) | 관세율표 | 분류사례 | 관세사·해외 | 주·해설 | 이미지
 *
 *   데이터는 useSearchData 1회 공유(탭 카운트 + 각 패널). "전체"는 MultiSearchResults
 *   에 data 를 주입해 중복 fetch 방지. 활성 탭은 상위(HeadingDetailPage)가 URL(?tab=)로 제어.
 *
 *   호 상세의 HeadingTabs(통칙/부주/류주/호해설/분류사례/이미지/관세사)와 별개 표면:
 *   이건 "검색어" 기준(키워드 매칭·호 횡단), HeadingTabs 는 "선택한 호" 기준.
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { useNarrowViewport } from "./useNarrowViewport";
import { useSearchData } from "./useSearchData";
import HistoryFunnelPanel from "./HistoryFunnelPanel";
import { HISTORY_FOCUS_EVENT, HISTORY_BACK_EVENT } from "./historySignalStore";
import MultiSearchResults, { CaseTable } from "./MultiSearchResults";
import TariffCompareTable from "./TariffCompareTable";
import AiClassificationWorkspace from "./AiClassificationWorkspace";
import { CLIP_IMG_BASE } from "./precedentAssets";
import ClassifyReportModal from "./ClassifyReportModal";
import { buildSearchReportData } from "./multiSearchData";
import { buildPrecedentGuide, guideForReport } from "./precedentGuide";
import { loadTreeFacts } from "./treeFactStore";
import AskDrawer, { DRAWER_RAIL_WIDTH } from "./AskDrawer";
import { clampDrawerWidth, DRAWER_DEFAULT_WIDTH, readDrawerOpen, readDrawerWidth, writeDrawerOpen, writeDrawerWidth } from "./askContextStore";
import { navigateToPrecedentDetail } from "./precedentNavigation";
import {
  buildHighlightSnippet,
  buildSearchHighlightTokens,
  highlightTokens,
} from "./highlightTokens";
import { isVerdictTabVisible } from "./verdictTab";

const TABS = [
  // PROVE — 자연어 사실추출 + 검수 결정트리 + 근거 그래프. 맨 앞.
  { id: "ai", label: "확정 검토" },
  { id: "all", label: "전체" },
  { id: "rate", label: "관세율표", countKey: "tariff" },
  { id: "cases", label: "분류사례", countKey: "cases" },
  { id: "agency", label: "관세사이력", countKey: "agency" },   // 54차: 개수는 이력 패널이 창 이벤트로 알린다
  { id: "foreign", label: "해외·사전심사", countKey: "foreign" },
  { id: "notes", label: "주·해설", countKey: "notes" },
  { id: "img", label: "이미지", countKey: "images" },
];

export default function SearchResultTabs({
  q,
  tariffData,
  subdivisionPreview,
  onRowClick,
  activeTab,
  onTab,
  lang,
  onToggleLang,
  onPrint,
  scrollContainerRef,
}) {
  const d = useSearchData(q, tariffData);
  // 54·55차: 관세사이력 탭 개수 — 패널이 `loginhs:history-count`로 알린, 이력이 가리키는 세번 수(문서 수가 아님).
  const [historyCount, setHistoryCount] = useState(null);
  useEffect(() => { setHistoryCount(null); }, [q]);
  useEffect(() => {
    const on = (e) => {
      const det = e.detail || {};
      if (String(det.q || "").trim() !== String(q || "").trim()) return;
      setHistoryCount(Number.isSafeInteger(det.count) ? det.count : null);
    };
    window.addEventListener("loginhs:history-count", on);
    return () => window.removeEventListener("loginhs:history-count", on);
  }, [q]);
  const tariffMatchTokens = buildSearchHighlightTokens(q, d.tariff?.matchTokens);
  const precedentMatchTokens = buildSearchHighlightTokens(q, d.precState?.matchTokens);
  const noteMatchTokens = buildSearchHighlightTokens(q, d.notesState?.matchTokens);
  const [aiReport, setAiReport] = useState(null); // PROVE 패널이 올린 검토보고서 코어
  const [showReport, setShowReport] = useState(false);
  const [proveResearchQuery, setProveResearchQuery] = useState("");
  // 확정 검토 탭은 기본 숨김(verdictTab.js). 딥링크·명시 실행(activeTab="ai")이면 보인다.
  const verdictVisible = isVerdictTabVisible() || activeTab === "ai";
  const tabs = verdictVisible ? TABS : TABS.filter((t) => t.id !== "ai");
  const active = tabs.some((t) => t.id === activeTab) ? activeTab : "all";
  const scrollPositionsRef = useRef(new Map());
  const scrollKey = useCallback((tabId) => `${q}\u0000${tabId}`, [q]);
  const rememberScroll = useCallback((tabId = active) => {
    const container = scrollContainerRef?.current;
    if (!container) return;
    scrollPositionsRef.current.set(scrollKey(tabId), container.scrollTop);
  }, [active, scrollContainerRef, scrollKey]);
  const changeTab = useCallback((tabId) => {
    if (tabId === active) return;
    rememberScroll(active);
    onTab?.(tabId);
  }, [active, onTab, rememberScroll]);
  // 지도 마디의 `관세사 이력 N%` 배지 → 관세사이력 탭(세번 고르기는 이력 패널이 같은 이벤트로 한다).
  useEffect(() => {
    const on = () => changeTab("agency");
    const back = () => changeTab("all");
    window.addEventListener(HISTORY_FOCUS_EVENT, on);
    window.addEventListener(HISTORY_BACK_EVENT, back);
    return () => { window.removeEventListener(HISTORY_FOCUS_EVENT, on); window.removeEventListener(HISTORY_BACK_EVENT, back); };
  }, [changeTab]);
  const [aiKey, setAiKey] = useState(null);
  // 대화 열(더 묻기) — 탭 바 아래·결과 왼쪽 접이식. 열림 상태는 저장해 다음 검색에도 유지한다. 좁은 화면에는 두지 않는다.
  const [drawerOpen, setDrawerOpen] = useState(readDrawerOpen);
  const toggleDrawer = useCallback((open) => { setDrawerOpen(open); writeDrawerOpen(open); }, []);
  // 대화 열과 결과 사이의 세로 손잡이 — 끌어서 폭을 정한다(280~640px, 저장). 두 번 누르면 기본 폭, 화살표 키로도 움직인다.
  const [drawerWidth, setDrawerWidth] = useState(readDrawerWidth);
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef(null);
  const startResize = useCallback((clientX) => {
    dragRef.current = { startX: clientX, startW: drawerWidth };
    setResizing(true);
    const onMove = (event) => {
      const x = event.touches ? event.touches[0].clientX : event.clientX;
      if (!dragRef.current || typeof x !== "number") return;
      setDrawerWidth(clampDrawerWidth(dragRef.current.startW + (x - dragRef.current.startX)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove); window.removeEventListener("touchend", onUp);
      setResizing(false);
      setDrawerWidth((w) => { writeDrawerWidth(w); return w; });
      dragRef.current = null;
    };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true }); window.addEventListener("touchend", onUp);
  }, [drawerWidth]);
  const nudgeResize = useCallback((delta) => { setDrawerWidth((w) => { const next = clampDrawerWidth(w + delta); writeDrawerWidth(next); return next; }); }, []);
  const resetResize = useCallback(() => { setDrawerWidth(DRAWER_DEFAULT_WIDTH); writeDrawerWidth(DRAWER_DEFAULT_WIDTH); }, []);
  // 전체 탭 카드의 "PROVE에서 근거 확인"은 사용자의 명시적 시작이므로 탭만 옮기지 않고
  // 게이트를 통과시켜 바로 실행한다(단순 탭 클릭 열람은 여전히 게이트 카드).
  const narrow = useNarrowViewport();
  // 대화 열 높이 — 창(100vh)이 아니라 가운데 스크롤 영역의 실제 높이에서 탭 바(52)·여백을 뺀다.
  // 100vh 기준은 앱 헤더만큼 길어져 맨 아래 상태 줄이 화면 밖으로 밀렸다(2026-09-14 사용자 보고).
  const [drawerHeight, setDrawerHeight] = useState(null);
  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return undefined;
    const measure = () => { const h = el.clientHeight; if (h > 0) setDrawerHeight(Math.max(240, h - 52 - 12)); };
    measure();
    if (typeof ResizeObserver === "undefined") { window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }
    const ro = new ResizeObserver(measure); ro.observe(el);
    return () => ro.disconnect();
  }, [scrollContainerRef]);
  const openProve = useCallback((options = {}) => {
    if (options.reuseWebResearch) setProveResearchQuery(q);
    setAiKey(q);
    changeTab("ai");
  }, [changeTab, q]);
  const openPrecedent = useCallback(
    (caseId, item) => {
      navigateToPrecedentDetail(caseId, {
        query: q,
        source: item?.source === "clip_foreign" ? "foreign" : "domestic",
      });
    },
    [q],
  );

  // PROVE 탭 — lazy + keep-alive. 자동 실행은 메인 검색 제출
  //   (sessionStorage loginhs_ai_run 신호)이나 전체 탭 카드의 명시 버튼일 때만.
  //   단순 탭 클릭 열람은 게이트 카드. (aiKey 선언은 openProve보다 위에 있어야 한다.)
  const aiActive = active === "ai";
  // 뒤로가기/탭 전환 뒤에도 완료된 AI 답변은 다시 생성하지 않고 즉시 복원한다.
  // AiModePanel이 저장한 스레드에 assistant 메시지가 있을 때만 실행된 것으로 본다.
  useEffect(() => {
    if (!aiActive || !q || aiKey === q) return;
    try {
      const raw = sessionStorage.getItem(`loginhs:ai-thread:${q}`);
      const saved = raw ? JSON.parse(raw) : null;
      const hasAnswer = Array.isArray(saved?.messages)
        && saved.messages.some((message) => message?.role === "assistant" && message?.content);
      if (hasAnswer) setAiKey(q);
    } catch {
      /* ignore malformed/private session storage */
    }
  }, [aiActive, aiKey, q]);
  useEffect(() => {
    if (!aiActive || !q) return;
    let runQ = null;
    try {
      runQ = sessionStorage.getItem("loginhs_ai_run");
    } catch {
      /* ignore */
    }
    if (runQ === q) {
      try {
        sessionStorage.removeItem("loginhs_ai_run");
      } catch {
        /* ignore */
      }
      setAiKey(q);
    }
  }, [aiActive, q]);

  useEffect(() => {
    setAiReport(null);
    setProveResearchQuery("");
  }, [q]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return undefined;
    const handleScroll = () => rememberScroll(active);
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [active, rememberScroll, scrollContainerRef]);

  useLayoutEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return undefined;
    const target = scrollPositionsRef.current.get(scrollKey(active)) || 0;
    const frame = window.requestAnimationFrame(() => {
      container.scrollTop = target;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [active, scrollContainerRef, scrollKey]);

  if (!q) return null;

  // 활성 탭 기준 검토보고서 코어 — PROVE는 패널 판정, 그 외 검색 탭은 종합 결과.
  // author/generated_at 은 여기서 균일 부여(시점 고정). decided_hsk 없으면 null(버튼 숨김).
  const decorate = (core) =>
    core && core.decided_hsk
      ? {
          ...core,
          author: { name: localStorage.getItem("userName") || "", company: "", email: "" },
          generated_at: new Date().toISOString(),
        }
      : null;
  const reportData =
    active === "ai"
      ? decorate(aiReport)
      : decorate(
          buildSearchReportData({
            q,
            tariff: d.tariff,
            cases: d.cases,
            rulings: d.rulings,
            notes: d.notesState.items,
            guide: reportGuide(q, subdivisionPreview),
          }),
        );

  return (
    <div className="audit-fade-in" style={{ marginTop: 0 }}>
      {/* 탭 바 (구글식: 왼쪽 탭 + 오른쪽 도구=영문/인쇄) */}
      <div
        role="tablist"
        aria-label="검색 결과 종류"
        style={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          borderBottom: "1px solid var(--audit-border)",
          overflowX: "auto",
          marginBottom: 12,
          position: "sticky",
          top: 0,
          zIndex: 35,
          background: "var(--audit-bg)",
          boxShadow: "0 4px 8px rgba(15, 23, 42, 0.06)",
        }}
      >
        {tabs.map((t) => {
          const isA = active === t.id;
          const cnt = t.id === "agency" ? historyCount : t.countKey ? d.counts[t.countKey] : null;
          const empty = cnt === 0;
          return (
            <button
              key={t.id}
              id={`search-result-tab-${t.id}`}
              role="tab"
              aria-selected={isA}
              aria-controls={`search-result-panel-${t.id}`}
              onClick={() => changeTab(t.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 13px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                whiteSpace: "nowrap",
                color: isA ? "var(--audit-text)" : "var(--audit-text-muted)",
                fontWeight: isA ? 700 : 500,
                borderBottom: isA ? "2px solid var(--audit-accent)" : "2px solid transparent",
                opacity: empty && !isA ? 0.5 : 1,
                marginBottom: -1,
              }}
            >
              {t.label}
              {t.countKey && (
                <span
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 12,
                    fontVariantNumeric: "tabular-nums",
                    padding: "1px 7px",
                    borderRadius: 999,
                    background: isA ? "var(--audit-accent-soft, rgba(37,99,235,.12))" : "var(--audit-bg-subtle)",
                    color: isA ? "var(--audit-accent)" : "var(--audit-text-muted)",
                  }}
                >
                  {cnt == null ? "…" : cnt}
                </span>
              )}
            </button>
          );
        })}
        {(q || onToggleLang || onPrint || reportData) && (
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: 6,
              flexShrink: 0,
              paddingLeft: 12,
              alignItems: "center",
            }}
          >
            {q && !narrow && (
              <button
                type="button"
                data-testid="ask-drawer-toggle"
                aria-pressed={drawerOpen}
                onClick={() => toggleDrawer(!drawerOpen)}
                style={ctrlBtn}
                title="이 물품에 대해 더 묻기 — 결과 왼쪽 대화 열을 접거나 폅니다"
              >
                {drawerOpen ? "◧ 대화 열 접기" : "◨ 대화 열"}
              </button>
            )}
            {verdictVisible && q && aiKey !== q && !narrow && (
              <button
                type="button"
                onClick={() => openProve()}
                style={proveBtn}
                title="이 검색어로 확정 검토를 시작합니다 (자연어 사실 추출 → 검수 결정트리 → 근거 → 검토보고서)"
              >
                확정 검토 · 보고서
              </button>
            )}
            {onToggleLang && (
              <button onClick={onToggleLang} style={ctrlBtn} title="한↔영 전환">
                {lang === "kor" ? "영문" : "한글"}
              </button>
            )}
            {onPrint && (
              <button onClick={onPrint} style={ctrlBtn} title="인쇄 미리보기">
                인쇄
              </button>
            )}
            {reportData && (
              <button
                onClick={() => setShowReport(true)}
                style={reportBtn}
                title="이 결과를 A4 품목분류 검토보고서로 (PDF 다운로드·이메일)"
              >
                검토보고서
              </button>
            )}
          </div>
        )}
      </div>

      {/* 좁은 화면: 다음 행동(PROVE 실행)을 손가락이 닿는 하단에 고정한다. */}
      {verdictVisible && narrow && q && aiKey !== q && active !== "ai" && (
        <div
          data-testid="mobile-prove-cta"
          style={{
            position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 40,
            padding: "8px 12px calc(8px + env(safe-area-inset-bottom, 0px))",
            background: "var(--audit-bg)",
            borderTop: "1px solid var(--audit-border)",
            boxShadow: "0 -4px 12px rgba(15, 23, 42, 0.08)",
            display: "flex", gap: 8, alignItems: "center",
          }}
        >
          <span style={{ flex: 1, fontSize: 12, color: "var(--audit-text-muted)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            「{q}」 사실을 확인했으면
          </span>
          <button type="button" onClick={() => openProve()} style={{ ...proveBtn, padding: "10px 18px", fontSize: 14 }}>
            확정 검토 · 보고서
          </button>
        </div>
      )}

      {/* 대화 열(왼쪽, 접이식) + 패널. 패널은 전부 mounted 상태로 유지해 탭별 결과·펼침·입력 상태를 보존한다. */}
      <div data-testid="results-with-drawer" data-drawer-width={narrow ? undefined : (drawerOpen ? drawerWidth : DRAWER_RAIL_WIDTH)}
        style={{ display: narrow ? "block" : "grid", gridTemplateColumns: narrow ? undefined : `${drawerOpen ? drawerWidth : DRAWER_RAIL_WIDTH}px ${drawerOpen ? "10px" : "0px"} minmax(0, 1fr)`, gap: 4, alignItems: "start", userSelect: resizing ? "none" : undefined, cursor: resizing ? "col-resize" : undefined }}>
      {!narrow && <AskDrawer q={q} open={drawerOpen} onToggle={toggleDrawer} width={drawerWidth} height={drawerHeight} />}
      {!narrow && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="대화 열 폭 조절"
          aria-valuemin={280}
          aria-valuemax={640}
          aria-valuenow={drawerWidth}
          tabIndex={drawerOpen ? 0 : -1}
          data-testid="ask-drawer-resizer"
          data-resizing={resizing || undefined}
          title="끌어서 대화 열 폭을 조절합니다 · 두 번 누르면 기본 폭"
          onMouseDown={(event) => { if (!drawerOpen) return; event.preventDefault(); startResize(event.clientX); }}
          onTouchStart={(event) => { if (!drawerOpen || !event.touches?.[0]) return; startResize(event.touches[0].clientX); }}
          onDoubleClick={resetResize}
          onKeyDown={(event) => { if (event.key === "ArrowLeft") { event.preventDefault(); nudgeResize(-16); } else if (event.key === "ArrowRight") { event.preventDefault(); nudgeResize(16); } else if (event.key === "Home") { event.preventDefault(); resetResize(); } }}
          style={{ gridColumn: 2, position: "sticky", top: 52, alignSelf: "start", width: drawerOpen ? 10 : 0, height: drawerHeight ? drawerHeight : "calc(100vh - 140px)", minHeight: 220, display: drawerOpen ? "flex" : "none", alignItems: "center", justifyContent: "center", cursor: "col-resize", touchAction: "none", outline: "none" }}
        >
          <span aria-hidden="true" style={{ width: resizing ? 3 : 2, height: 36, borderRadius: 2, background: resizing ? "var(--audit-accent)" : "var(--audit-border-strong, #cdd1d8)", opacity: resizing ? 1 : 0.6, transition: "background .12s, width .12s, opacity .12s" }} />
        </div>
      )}
      <div data-testid="results-column" style={{ minWidth: 0, gridColumn: narrow ? undefined : 3 }}>
      <KeepAlivePanel id="all" active={active}>
        <MultiSearchResults
          q={q}
          tariffData={tariffData}
          subdivisionPreview={subdivisionPreview}
          onRowClick={onRowClick}
          onPrecedentOpen={openPrecedent}
          onOpenProve={verdictVisible ? openProve : undefined}
          data={d}
        />
      </KeepAlivePanel>

      <KeepAlivePanel id="rate" active={active}>
        <TabWrap title="관세율표 · 세율 비교" sub={`매칭 세번 ${d.counts.tariff ?? "…"}건 — 세번 클릭 시 상세로`}>
          <TariffCompareTable
            items={d.tariff ? d.tariff.items : []}
            headingMatches={d.tariff ? d.tariff.headingMatches : []}
            matchTokens={tariffMatchTokens}
            onRowClick={onRowClick}
          />
        </TabWrap>
      </KeepAlivePanel>

      <KeepAlivePanel id="cases" active={active}>
        <TabWrap title="분류사례 · 국내(관세청)" sub="clip_kcs · bigdata — 호 횡단" state={d.precState} onRetry={d.retry.prec}>
          {d.cases.length ? (
            <CaseTable
              items={d.cases}
              onOpen={openPrecedent}
              matchTokens={precedentMatchTokens}
            />
          ) : (
            <Empty>매칭 분류사례 없음</Empty>
          )}
        </TabWrap>
      </KeepAlivePanel>

      <KeepAlivePanel id="agency" active={active}>
        <HistoryFunnelPanel searchQuery={q} />
      </KeepAlivePanel>

      <KeepAlivePanel id="foreign" active={active}>
        <TabWrap title="해외·사전심사 분류사례" sub="사전심사 · 외국 결정례 — 관세사 신고이력과 별도" state={d.precState} onRetry={d.retry.prec}>
          {d.rulings.length ? (
            <CaseTable
              items={d.rulings}
              onOpen={openPrecedent}
              showSourceBadge
              matchTokens={precedentMatchTokens}
            />
          ) : (
            <Empty>매칭 결정례 없음</Empty>
          )}
        </TabWrap>
      </KeepAlivePanel>

      <KeepAlivePanel id="notes" active={active}>
        <NotesTab
          notesState={d.notesState}
          lawsState={d.lawsState}
          onRetry={d.retry.notes}
          matchTokens={noteMatchTokens}
        />
      </KeepAlivePanel>

      <KeepAlivePanel id="img" active={active}>
        <TabWrap title="이미지" sub="분류사례 첨부 사진" state={d.precState} onRetry={d.retry.prec}>
          <ImageGrid
            images={d.images}
            onOpen={openPrecedent}
            matchTokens={precedentMatchTokens}
          />
        </TabWrap>
      </KeepAlivePanel>

      {/* PROVE 게이트 — 탭 클릭 열람 시 자동 실행 금지, 명시적 버튼으로 시작. */}
      <KeepAlivePanel id="ai" active={active}>
        {aiKey !== q && (
          <div
            style={{
              border: "1px solid var(--audit-border)",
              borderRadius: 8,
              padding: "28px 20px",
              textAlign: "center",
              background: "var(--audit-bg-elevated, var(--audit-bg))",
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
              「{q}」 확정 검토 · 보고서
            </div>
            <div style={{ fontSize: 12.5, color: "var(--audit-text-muted)", marginBottom: 14, lineHeight: 1.6 }}>
              자연어를 분류 사실로 해체하고 검수 결정트리와 근거 그래프로 판정합니다.
              <br />
              PROVE가 먼저 판정하며, 사실이 부족하면 AI 웹검증을 자동 실행합니다.
            </div>
            <button
              onClick={() => setAiKey(q)}
              style={{
                border: "none",
                borderRadius: 6,
                padding: "9px 22px",
                background: "var(--audit-accent)",
                color: "#fff",
                fontSize: 13.5,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              확정 검토 · 보고서 시작 →
            </button>
          </div>
        )}

        {aiKey === q && (
          <AiClassificationWorkspace
            q={q}
            productDetails={tariffData?.productDetails}
            onNav={onRowClick}
            onReportData={setAiReport}
            autoResearch={proveResearchQuery === q}
          />
        )}
      </KeepAlivePanel>

      </div>
      </div>

      {showReport && reportData && (
        <ClassifyReportModal reportData={reportData} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
}

function KeepAlivePanel({ id, active, children }) {
  const selected = active === id;
  return (
    <div
      id={`search-result-panel-${id}`}
      role="tabpanel"
      aria-labelledby={`search-result-tab-${id}`}
      aria-hidden={!selected}
      style={{ display: selected ? "block" : "none" }}
    >
      {children}
    </div>
  );
}

// ── 공용 패널 래퍼 (제목 + 실패/재시도) ──
function TabWrap({ title, sub, state, onRetry, children }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "2px 0 12px" }}>
        <h2 style={{ margin: 0, fontSize: 16, letterSpacing: "-.01em" }}>{title}</h2>
        {sub && <span style={{ color: "var(--audit-text-muted)", fontSize: 12.5 }}>{sub}</span>}
      </div>
      {state && state.error ? (
        <div style={{ fontSize: 13, color: "#d93025", display: "flex", gap: 10, alignItems: "center" }}>
          불러오기 실패
          {onRetry && (
            <button onClick={onRetry} style={retryBtn}>재시도</button>
          )}
        </div>
      ) : state && state.loading ? (
        <Loading />
      ) : (
        children
      )}
    </div>
  );
}

// ── 주·해설 탭 (부주/류주/호해설 칩 + 관련법령 링크) ──
const NOTE_KINDS = ["부주", "류주", "호해설"];
function noteKind(it) {
  const byType = { sctManl: "부주", hstdManl: "류주", hsfdManl: "호해설" };
  if (it.note_type && byType[it.note_type]) return byType[it.note_type];
  const l = String(it.label || "");
  return NOTE_KINDS.find((k) => l.includes(k)) || null;
}
export function NotesTab({ notesState, lawsState, onRetry, matchTokens = [] }) {
  const [kind, setKind] = useState("전체");
  const items = notesState.items || [];
  const counts = { 전체: items.length };
  NOTE_KINDS.forEach((k) => (counts[k] = items.filter((it) => noteKind(it) === k).length));
  const shown = kind === "전체" ? items : items.filter((it) => noteKind(it) === kind);
  const lawN = (lawsState.items || []).length;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "2px 0 12px" }}>
        <h2 style={{ margin: 0, fontSize: 16, letterSpacing: "-.01em" }}>주 · 해설</h2>
        <span style={{ color: "var(--audit-text-muted)", fontSize: 12.5 }}>notes-search — 키워드 매칭</span>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        {["전체", ...NOTE_KINDS].map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            style={{
              border: "1px solid " + (kind === k ? "var(--audit-accent)" : "var(--audit-border)"),
              background: kind === k ? "var(--audit-accent-soft, rgba(37,99,235,.12))" : "var(--audit-bg)",
              color: kind === k ? "var(--audit-accent)" : "var(--audit-text-muted)",
              fontWeight: kind === k ? 700 : 500,
              borderRadius: 999,
              padding: "5px 13px",
              fontSize: 12.5,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {k} {counts[k] || 0}
          </button>
        ))}
        {lawN > 0 && (
          <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--audit-text-muted)" }}>
            관련법령 <b className="mono">{lawN}</b>건
          </span>
        )}
      </div>
      {notesState.error ? (
        <div style={{ fontSize: 13, color: "#d93025", display: "flex", gap: 10, alignItems: "center" }}>
          불러오기 실패<button onClick={onRetry} style={retryBtn}>재시도</button>
        </div>
      ) : notesState.loading ? (
        <Loading />
      ) : shown.length === 0 ? (
        <Empty>{kind === "전체" ? "매칭 주·해설 없음" : `${kind} 매칭 없음`}</Empty>
      ) : (
        shown.map((it, i) => {
          const clean = String(it.desc || "")
            .replace(/<a\s[^>]*>([^<]*)<\/a>/g, "$1")
            .replace(/<\/?[a-z][^>]*>/gi, "");
          const visible = buildHighlightSnippet(clean, matchTokens) || clean;
          return (
            <div key={i} style={{ borderTop: i > 0 ? "1px solid var(--audit-border)" : "none", padding: "9px 0", fontSize: 13, lineHeight: "19px" }}>
              <div style={{ fontFamily: "JetBrains Mono, monospace", color: "var(--audit-text-muted)", marginBottom: 4 }}>
                {noteKind(it) && (
                  <span style={{ border: "1px solid var(--audit-border)", borderRadius: 3, padding: "0 5px", marginRight: 6 }}>
                    {noteKind(it)}
                  </span>
                )}
                {highlightTokens(it.label, matchTokens)}
              </div>
              <div style={{ lineHeight: 1.6, whiteSpace: "pre-line" }}>
                {highlightTokens(visible, matchTokens)}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── 이미지 그리드 (CLIP 핫링크 재사용) ──
export function ImageGrid({ images, onOpen, matchTokens = [] }) {
  if (!images || images.length === 0) {
    return <Empty>이미지가 있는 분류사례 없음 (관세청 CLIP 이미지 미보유)</Empty>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
      {images.flatMap((it) =>
        it.atch_ids.map((aid, k) => {
          const matchSnippet = it.match_snippet
            || buildHighlightSnippet(it.description, matchTokens);
          return (
          <button
            type="button"
            key={`${it.case_id}-${k}`}
            onClick={() => onOpen?.(it.case_id, it)}
            title={`${it.hsk || ""} ${it.label || ""} 분류사례 상세 보기`}
            aria-label={`${it.hsk || it.label || "분류사례"} 상세 보기`}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              padding: 0,
              border: 0,
              background: "transparent",
              color: "inherit",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <img
              src={CLIP_IMG_BASE + encodeURIComponent(aid)}
              alt={it.label || it.case_id}
              loading="lazy"
              style={{
                width: 132,
                height: 132,
                objectFit: "cover",
                border: "1px solid var(--audit-border)",
                borderRadius: 4,
                background: "var(--audit-bg-subtle)",
              }}
            />
            <span
              style={{
                fontSize: 12,
                color: "var(--audit-text-muted)",
                maxWidth: 132,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontFamily: "JetBrains Mono, monospace",
              }}
            >
              {it.hsk || ""}
            </span>
            {it.label && (
              <span
                style={{
                  fontSize: 11.5,
                  color: "var(--audit-text)",
                  maxWidth: 132,
                  lineHeight: 1.35,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {highlightTokens(it.label, matchTokens)}
              </span>
            )}
            {matchSnippet && (
              <span
                style={{
                  fontSize: 11,
                  color: "var(--audit-text-muted)",
                  maxWidth: 132,
                  lineHeight: 1.35,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                본문: {highlightTokens(matchSnippet, matchTokens)}
              </span>
            )}
          </button>
          );
        }),
      )}
    </div>
  );
}

function Loading() {
  return <div style={{ color: "var(--audit-text-muted)", fontSize: 13, lineHeight: "19px", padding: "6px 0" }}>불러오는 중…</div>;
}
function Empty({ children }) {
  return (
    <div style={{ padding: 12, color: "var(--audit-text-muted)", fontSize: 13, lineHeight: "19px", fontStyle: "italic", border: "1px dashed var(--audit-border)", borderRadius: 4, background: "var(--audit-bg-subtle)" }}>
      {children}
    </div>
  );
}
const retryBtn = {
  border: "1px solid var(--audit-border)",
  background: "transparent",
  color: "#d93025",
  fontSize: 12,
  borderRadius: 4,
  cursor: "pointer",
  padding: "2px 10px",
  fontFamily: "inherit",
};
// PROVE 실행 — 탭바 우측 1차 액션. 검색바의 모드 선택을 대신한다(2026-09-03).
const proveBtn = {
  border: "none",
  borderRadius: 6,
  padding: "5px 14px",
  background: "var(--audit-accent)",
  color: "#fff",
  fontSize: 12.5,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap",
};
/** 사전 점검 카드가 본 결정트리·결정례로 검토보고서용 판정 가이드를 만든다. 카드가 없으면 null. */
function reportGuide(q, preview) {
  if (!q || !preview || preview.q !== q || !preview.row) return null;
  const tree = preview.row.decision_tree_shadow;
  if (!tree || typeof tree !== "object") return null;
  const hints = preview.row.precedent_hints && typeof preview.row.precedent_hints === "object" ? preview.row.precedent_hints : null;
  return guideForReport(buildPrecedentGuide({ tree, hints, answers: loadTreeFacts(q) }));
}

const ctrlBtn = {
  border: "1px solid var(--audit-border)",
  background: "transparent",
  color: "var(--audit-text-muted)",
  fontSize: 12,
  borderRadius: 5,
  cursor: "pointer",
  padding: "4px 10px",
  fontFamily: "inherit",
};
// 검토보고서 — 탭바 우측 기본 액션(강조: accent 테두리·글자)
const reportBtn = {
  ...ctrlBtn,
  borderColor: "var(--audit-accent)",
  color: "var(--audit-accent)",
  fontWeight: 700,
  whiteSpace: "nowrap",
};
